import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { 
  Sun, Moon, Zap, Target, CheckCircle2, Clock, AlertTriangle,
  ArrowRight, Plus, Calendar, Bell, DollarSign, TrendingUp, X, Trash2, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import BoussoleCard from '../components/BoussoleCard';
import DebriefCard from '../components/DebriefCard';
import EngagementsWidget from '../components/EngagementsWidget';
import PWAInstallBanner from '../components/PWAInstallBanner';
import ModuleSuggestionBanner from '../components/ModuleSuggestionBanner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { t, lang } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [currentMode, setCurrentMode] = useState('decision');
  const [loading, setLoading] = useState(true);
  const [upcomingEngagements, setUpcomingEngagements] = useState([]);
  const [showManagePriorities, setShowManagePriorities] = useState(false);
  const [allPriorities, setAllPriorities] = useState([]);
  const [deletingPriority, setDeletingPriority] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const tzOffset = new Date().getTimezoneOffset();
        const [dashRes, modeRes, briefRes, notifRes, engRes] = await Promise.all([
          api.get('/api/dashboard'),
          api.get(`/api/suggested-mode?tz_offset=${tzOffset}`),
          api.get('/api/briefing'),
          api.get('/api/notifications'),
          api.get('/api/engagements')
        ]);
        setDashboard(dashRes.data);
        setCurrentMode(modeRes.data.mode);
        setBriefing(briefRes.data);
        setNotifications(notifRes.data);

        // === Filtrage frontend des engagements à venir (même logique que /engagements) ===
        const all = [
          ...(engRes.data.today || []),
          ...(engRes.data.upcoming || []),
          ...(engRes.data.history || []),
        ];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const closedStatuts = ['complete', 'reporte', 'ignore', 'annule'];
        const upcoming = all.filter(eng => {
          if (closedStatuts.includes(eng.statut)) return false;
          const engDate = new Date(eng.date_engagement + 'T00:00:00');
          engDate.setHours(0, 0, 0, 0);
          return engDate.getTime() > today.getTime();
        }).sort((a, b) =>
          (a.date_engagement + (a.heure_debut || '')).localeCompare(b.date_engagement + (b.heure_debut || ''))
        );
        console.log('[Dashboard] upcoming engagements:', upcoming.length, upcoming.map(e => `${e.date_engagement} ${e.heure_debut}`));
        setUpcomingEngagements(upcoming);
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [api]);

  const dismissNotification = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const openManagePriorities = async () => {
    try {
      const res = await api.get('/api/priorities');
      setAllPriorities(res.data || []);
      setShowManagePriorities(true);
    } catch (e) {
      toast.error(t('common.error'));
    }
  };

  const deletePriorityById = async (id) => {
    setDeletingPriority(id);
    try {
      await api.delete(`/api/priorities/${id}`);
      setAllPriorities(prev => prev.filter(p => p.id !== id));
      // Refresh dashboard priorities (top 3)
      const dashRes = await api.get('/api/dashboard');
      setDashboard(dashRes.data);
      toast.success(t('dashboard.priorityDeleted'));
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setDeletingPriority(null);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t('dashboard.greeting.morning');
    if (hour >= 12 && hour < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'morning': return <Sun className="w-5 h-5" />;
      case 'evening': return <Moon className="w-5 h-5" />;
      default: return <Zap className="w-5 h-5" />;
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'morning': return 'text-atlasYellow border-atlasYellow bg-atlasYellow/10';
      case 'evening': return 'text-atlasPurple border-atlasPurple bg-atlasPurple/10';
      default: return 'text-atlasCyan border-atlasCyan bg-atlasCyan/10';
    }
  };

  const startSession = () => {
    navigate('/chat');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-atlasCyan animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const priorities = dashboard?.priorities || [];
  const recentDecisions = dashboard?.recent_decisions || [];

  return (
    <div data-testid="dashboard-page" className="p-6 lg:p-8 space-y-8 max-w-7xl mx-auto">
      <ModuleSuggestionBanner />
      {/* PWA install banner (mobile only, hidden if already installed) */}
      <div className="-mb-4">
        <PWAInstallBanner />
      </div>

      {/* Monday Compass Card */}
      <BoussoleCard />

      {/* Friday Debrief Card */}
      <DebriefCard />

      {/* Alerts Banner */}
      {briefing?.alerts?.length > 0 && (
        <div className="space-y-2">
          {briefing.alerts.map((alert, i) => (
            <div 
              key={i}
              className={`glass-card p-4 flex items-center justify-between ${
                alert.type === 'alert' ? 'border-atlasRed/50' : 'border-atlasYellow/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${alert.type === 'alert' ? 'text-atlasRed' : 'text-atlasYellow'}`} />
                <div>
                  <p className="text-white font-medium">{alert.title}</p>
                  <p className="text-atlasTextSecondary text-sm">{alert.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {briefing?.greeting || `${getGreeting()}, `}<span className="text-atlasCyan">{user?.name?.split(' ')[0] || 'Leader'}</span>
          </h1>
          <p className="text-atlasTextSecondary">
            {user?.business_context ? user.business_context.slice(0, 60) + '...' : t('dashboard.readyMessage')}
          </p>
        </div>

        {/* Mode Selector */}
        <div className="flex items-center gap-2">
          {['morning', 'decision', 'evening'].map((mode) => (
            <button
              key={mode}
              data-testid={`mode-${mode}-btn`}
              onClick={() => setCurrentMode(mode)}
              className={`mode-btn flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                currentMode === mode 
                  ? getModeColor(mode)
                  : 'border-atlasBorder text-atlasTextSecondary hover:border-white/20'
              }`}
            >
              {getModeIcon(mode)}
              <span className="atlas-brand text-sm">{t(`dashboard.modeName.${mode}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Business Metrics Quick View */}
      {(briefing?.stats?.cash_flow || briefing?.stats?.mrr || briefing?.stats?.active_leads) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {briefing.stats.mrr && (
            <div className="glass-card p-4 flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-white atlas-mono">{lang === 'fr' ? '' : '€'}{briefing.stats.mrr.toLocaleString()}{lang === 'fr' ? ' €' : ''}</p>
                <p className="text-xs text-atlasTextSecondary">{t('dashboard.businessMetricsLabels.mrr')}</p>
              </div>
            </div>
          )}
          {briefing.stats.cash_flow && (
            <div className="glass-card p-4 flex items-center gap-3">
              <TrendingUp className={`w-5 h-5 ${briefing.stats.runway_months < 6 ? 'text-atlasYellow' : 'text-green-500'}`} />
              <div>
                <p className="text-2xl font-bold text-white atlas-mono">{lang === 'fr' ? '' : '€'}{briefing.stats.cash_flow.toLocaleString()}{lang === 'fr' ? ' €' : ''}</p>
                <p className="text-xs text-atlasTextSecondary">{t('dashboard.businessMetricsLabels.cashFlow')}</p>
              </div>
            </div>
          )}
          {briefing.stats.runway_months && (
            <div className="glass-card p-4 flex items-center gap-3">
              <Clock className={`w-5 h-5 ${briefing.stats.runway_months < 3 ? 'text-atlasRed' : briefing.stats.runway_months < 6 ? 'text-atlasYellow' : 'text-green-500'}`} />
              <div>
                <p className="text-2xl font-bold text-white atlas-mono">{briefing.stats.runway_months}</p>
                <p className="text-xs text-atlasTextSecondary">{t('dashboard.businessMetricsLabels.runway')}</p>
              </div>
            </div>
          )}
          {briefing.stats.active_leads && (
            <div className="glass-card p-4 flex items-center gap-3">
              <Target className="w-5 h-5 text-atlasCyan" />
              <div>
                <p className="text-2xl font-bold text-white atlas-mono">{briefing.stats.active_leads}</p>
                <p className="text-xs text-atlasTextSecondary">{t('dashboard.businessMetricsLabels.activeLeads')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Action */}
      <div className="glass-card p-6 glow-cyan">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1">{t('dashboard.startSession')}</h2>
            <p className="text-atlasTextSecondary">
              {t(`dashboard.modeDescription.${currentMode}`)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate('/business')}
              className="border-atlasTextSecondary text-atlasTextSecondary hover:text-white"
            >
              {t('dashboard.updateMetrics')}
            </Button>
            <Button 
              data-testid="start-session-btn"
              onClick={startSession}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              {t('dashboard.talkToAtlas')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasYellow/10 rounded-lg">
              <Clock className="w-5 h-5 text-atlasYellow" />
            </div>
            <span className="text-atlasTextSecondary text-sm">{t('dashboard.stats.pending')}</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="pending-decisions-count">
            {stats.pending_decisions || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">{t('dashboard.stats.decisions')}</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-atlasTextSecondary text-sm">{t('dashboard.stats.completed')}</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="completed-decisions-count">
            {stats.completed_decisions || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">{t('dashboard.stats.decisions')}</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasCyan/10 rounded-lg">
              <Target className="w-5 h-5 text-atlasCyan" />
            </div>
            <span className="text-atlasTextSecondary text-sm">{t('dashboard.stats.active')}</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="active-commitments-count">
            {stats.active_commitments || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">{t('dashboard.stats.commitments')}</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasRed/10 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-atlasRed" />
            </div>
            <span className="text-atlasTextSecondary text-sm">{t('dashboard.stats.overdue')}</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="overdue-commitments-count">
            {stats.overdue_commitments || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">{t('dashboard.stats.commitments')}</p>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Priorities */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">{t('dashboard.priorities')}</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/decisions')}
              className="text-atlasCyan hover:text-atlasCyan/80"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {priorities.length > 0 ? (
            <ul className="space-y-3">
              {priorities.map((p, i) => (
                <li key={p.id} className="flex items-start gap-3" data-testid={`priority-${i}`}>
                  <span className="text-atlasCyan font-bold atlas-mono">#{p.rank}</span>
                  <span className="text-white">{p.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-atlasTextSecondary text-sm">{t('dashboard.noPriorities')}</p>
          )}
          {/* Lien discret pour gérer les priorités */}
          <button
            data-testid="manage-priorities-link"
            onClick={openManagePriorities}
            className="mt-4 text-xs text-atlasTextSecondary hover:text-atlasCyan transition-colors flex items-center gap-1.5"
          >
            <Settings2 className="w-3 h-3" />
            {t('dashboard.managePriorities')}
          </button>
        </div>

        {/* Engagements Today */}
        <EngagementsWidget />

        {/* Recent Decisions */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">{t('dashboard.recentDecisions')}</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/decisions')}
              className="text-atlasCyan hover:text-atlasCyan/80"
            >
              {t('dashboard.viewAll')}
            </Button>
          </div>
          {recentDecisions.length > 0 ? (
            <ul className="space-y-3">
              {recentDecisions.slice(0, 4).map((d, i) => (
                <li key={d.id} className="flex items-center justify-between" data-testid={`decision-${i}`}>
                  <span className="text-white truncate mr-2">{d.title}</span>
                  <span className={`text-xs px-2 py-1 rounded-full status-${d.status}`}>
                    {t(`tracker.status.${d.status}`)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-atlasTextSecondary text-sm">{t('dashboard.noDecisions')}</p>
          )}
        </div>

        {/* Upcoming Engagements (using new engagements feature) */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">{t('dashboard.upcomingCommitments')}</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/engagements')}
              className="text-atlasCyan hover:text-atlasCyan/80"
            >
              {t('dashboard.viewAll')}
            </Button>
          </div>
          {upcomingEngagements.length > 0 ? (
            <ul className="space-y-3">
              {upcomingEngagements.slice(0, 4).map((e, i) => (
                <li key={e.id} className="flex items-center justify-between" data-testid={`upcoming-engagement-${i}`}>
                  <div className="flex items-center gap-2 truncate mr-2">
                    <Calendar className="w-4 h-4 text-atlasTextSecondary flex-shrink-0" />
                    <span className="text-white truncate">{e.description}</span>
                  </div>
                  <span className="text-xs text-atlasTextSecondary atlas-mono whitespace-nowrap">
                    {format(new Date(e.date_engagement + 'T00:00:00'), 'd MMM')} · {e.heure_debut}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-atlasTextSecondary text-sm">{t('dashboard.noCommitments')}</p>
          )}
        </div>
      </div>

      {/* Modal Gérer les priorités */}
      {showManagePriorities && (
        <div className="fixed inset-0 z-[100] bg-atlasBg/90 backdrop-blur-sm flex items-center justify-center p-6" data-testid="manage-priorities-modal">
          <div className="glass-card p-6 w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{t('dashboard.managePriorities')}</h3>
              <button
                onClick={() => setShowManagePriorities(false)}
                className="p-1 text-atlasTextSecondary hover:text-white"
                data-testid="close-manage-priorities"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-atlasTextSecondary mb-3">{t('dashboard.managePrioritiesHint')}</p>
            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              {allPriorities.length === 0 ? (
                <p className="text-atlasTextSecondary text-sm py-6 text-center">{t('dashboard.noPriorities')}</p>
              ) : (
                <ul className="space-y-2">
                  {allPriorities.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-atlasBorder"
                      data-testid={`manage-priority-${p.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-atlasCyan font-bold atlas-mono text-sm flex-shrink-0">#{p.rank}</span>
                        <span className="text-white text-sm truncate">{p.title}</span>
                      </div>
                      <button
                        onClick={() => deletePriorityById(p.id)}
                        disabled={deletingPriority === p.id}
                        className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors flex-shrink-0"
                        data-testid={`delete-priority-${p.id}`}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
